/*
 * Note that all these functions may be run on both client and server
 */
import { validationDisabled, streetTypes } from '@/lib/const';

export const getLocale = () => {
  if (typeof window === 'undefined') {
    return 'en-CA';
  } else {
    return window?.navigator?.language || 'en-CA';
  }
};

/**
 * Escapes a string for regular expression use
 * See: https://stackoverflow.com/a/9310752
 * @param {string} text The string to escape
 * @returns A version safe to use in regular expressions
 */
export function escapeRegExp(text) {
  return text.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&');
}

/**
 * Gets the parts of a locale-specific decimal format
 * @returns {object} An object with decimal (e.g. period or comma) and group properties (space, comma, or period)
 */
const numFormatParts = (lng) => {
  const orgNum = 1000.1;
  // Prefer Canadian variants of languages
  if (typeof lng === 'string' && lng.length === 2) {
    lng = [`${lng}-CA`, lng];
  }
  const parts = Intl.NumberFormat(lng).formatToParts(orgNum);
  return {
    decimal: parts.find((p) => p.type === 'decimal')?.value || '.',
    group: parts.find((p) => p.type === 'group')?.value || null
  };
};

/**
 * Parses a float value according to the user's locale
 * @param {string|number} val The number to interpret
 * @returns number A decimal representation of that number according to the user's locale
 */
export const parseLocaleFloat = (val, lng = 'en') => {
  const str = `${val}`;
  const numParts = numFormatParts(lng);
  const pattern = new RegExp(
    `^([^${escapeRegExp(numParts.decimal)}]*)(${escapeRegExp(
      numParts.decimal
    )}(.*))?$`
  );
  const parts = pattern.exec(str);
  const whole = `${parts[1] || 0}`.replace(/[^0-9]+/g, '');
  const decimal = `${parts[3] || 0}`.replace(/[^0-9]+/g, '');
  return parseInt(whole) + parseFloat(`0.${decimal}`) || 0;
};

/**
 * Formats a numeric value into a currency
 * @param {Number} amount The number value to format
 * @param {Bool} showDollar Whether to prepend the dollar sign (default: true)
 * @param {Bool} showCents Whether to show cents (default: false)
 * @returns A string in currency format
 */
export const formatCurrency = (
  amount,
  showDollar = true,
  showCents = false,
  lng
) => {
  // Prefer Canadian variants of languages
  if (typeof lng === 'string' && lng.length === 2) {
    lng = [`${lng}-CA`, lng];
  }
  const currencyFormatter = new Intl.NumberFormat(lng || getLocale(), {
    style: 'currency',
    currency: 'CAD',
    currencyDisplay: 'narrowSymbol'
  });
  return currencyFormatter
    .formatToParts(amount)
    .filter(
      (p) =>
        (showDollar || p.type !== 'currency') &&
        (showCents || (p.type !== 'decimal' && p.type !== 'fraction'))
    )
    .map((p) => p.value)
    .join('')
    .trim();
};

export const formatDate = (date, lng) => {
  if (!(date instanceof Date)) {
    date = new Date(date);
  }
  // Prefer Canadian variants of languages
  if (typeof lng === 'string' && lng.length === 2) {
    lng = [`${lng}-CA`, lng];
  }

  return date.toLocaleString(lng || getLocale(), {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  });
};

export const formatTime = (date, lng) => {
  if (!(date instanceof Date)) {
    date = new Date(date);
  }
  // Prefer Canadian variants of languages
  if (typeof lng === 'string' && lng.length === 2) {
    lng = [`${lng}-CA`, lng];
  }
  return date.toLocaleString(lng || getLocale(), {
    hour: 'numeric',
    minute: 'numeric',
    hour12: true
  });
};

export const formatDateTime = (date, lng) => {
  if (!(date instanceof Date)) {
    date = new Date(date);
  }
  // Prefer Canadian variants of languages
  if (typeof lng === 'string' && lng.length === 2) {
    lng = [`${lng}-CA`, lng];
  }

  return date.toLocaleString(lng || getLocale(), {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: 'numeric',
    minute: 'numeric',
    hour12: true
  });
};

export const formatFriendlyDate = (date, lng) => {
  if (!(date instanceof Date)) {
    date = new Date(date);
  }
  // Prefer Canadian variants of languages
  if (typeof lng === 'string' && lng.length === 2) {
    lng = [`${lng}-CA`, lng];
  }

  return date.toLocaleString(lng || getLocale(), {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
};

export const fuzzyDate = (date, t) => {
  if (!(date instanceof Date)) {
    date = new Date(date);
  }
  const time = Math.round((Date.now() - date.getTime()) / 1000);
  const isFuture = time < 0;
  if (time < 86400) {
    // <1 day
    return isFuture ? t('date.soon', 'Soon') : t('date.today', 'Today');
  } else if (time < 172800) {
    // <2 days
    return isFuture
      ? t('date.tomorrow', 'Tomorrow')
      : t('date.yesterday', 'Yesterday');
  } else if (time < 604800) {
    // <1 week
    const weekday = [
      t('date.sunday', 'Sunday'),
      t('date.monday', 'Monday'),
      t('date.tuesday', 'Tuesday'),
      t('date.wednesday', 'Wednesday'),
      t('date.thursday', 'Thursday'),
      t('date.friday', 'Friday'),
      t('date.saturday', 'Saturday')
    ];
    return isFuture
      ? t('date.next', 'Next {{weekday}}', { weekday: weekday[date.getDay()] })
      : t('date.last', 'Last {{weekday}}', { weekday: weekday[date.getDay()] });
  } else if (time < 2629746) {
    // <1 Month
    const numWeeks = Math.round(time / 604800);
    if (numWeeks === 1) {
      return isFuture
        ? t('date.weekFromNow', '1 week from now')
        : t('date.weekAgo', '1 week ago');
    } else {
      return isFuture
        ? t('date.weeksFromNow', '{{numWeeks}} weeks from now', { numWeeks })
        : t('date.weeksAgo', '{{numWeeks}} weeks ago', { numWeeks });
    }
  } else if (time < 31556952) {
    // <1 Year
    const numMonths = Math.round(time / 2629746);
    if (numMonths === 1) {
      return isFuture
        ? t('date.monthFromNow', '1 month from now')
        : t('date.monthAgo', '1 month ago');
    } else {
      return isFuture
        ? t('date.monthsFromNow', '{{numMonths}} months from now', {
            numMonths
          })
        : t('date.monthsAgo', '{{numMonths}} months ago', { numMonths });
    }
  } else {
    const numYears = Math.round(time / 31556952);
    if (numYears === 1) {
      return isFuture
        ? t('date.yearFromNow', '1 year from now')
        : t('date.yearAgo', '1 year ago');
    } else {
      return isFuture
        ? t('date.yearsFromNow', '{{numYears}} years from now', { numYears })
        : t('date.yearsAgo', '{{numYears}} years ago', { numYears });
    }
  }
};

/**
 * Gets the offset in ms of a particular timezone currently.
 * @param {String} tz The timezone offset
 * @returns The number of miliseconds it is away from GMT
 */
export const getTimezoneOffset = (
  tz = 'America/Vancouver',
  asOf = new Date()
) => {
  // Get parts of the local time in the target time zone as a string
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  });

  const parts = formatter.formatToParts(asOf);
  const getPart = (type) => parts.find((p) => p.type === type)?.value;

  // Create a "local" time in the target time zone as if it were in UTC
  const localTime = Date.UTC(
    parseInt(getPart('year')),
    parseInt(getPart('month')) - 1,
    parseInt(getPart('day')),
    parseInt(getPart('hour')),
    parseInt(getPart('minute')),
    parseInt(getPart('second')),
    asOf.getMilliseconds()
  );

  // Difference between UTC and the time zone
  return localTime - asOf.getTime();
};

export const formatPhone = (phone) => {
  if (!phone) {
    return '';
  }

  return `${phone}`
    .replace(/(^\+1|[^0-9]+)/g, '')
    .replace(/^([0-9]{3})([0-9]{3})(.+)$/, '($1) $2-$3');
};

export const handleThrow = (err, clientMessage) => {
  if (err) {
    console.error('Error:', err);
  }
  return {
    error: true,
    message: clientMessage || 'An unknown error occurred',
    detail: err?.message
  };
};

export const nameSplit = (name) => {
  const nameParts = `${name}`.trim().split(/\s+/g);
  if (nameParts.length === 1) {
    return ['', nameParts[0]];
  } else {
    return [nameParts[0], nameParts.slice(1).join(' ')];
  }
};

export const avatarInitials = (name) =>
  `${name}`
    .split(/\s+/g)
    .filter(Boolean)
    .map((s) => s[0])
    .slice(-2)
    .join('')
    .toUpperCase();

/**
 * Serializes a sort object for URL
 * @param {Object} obj Key-value map in the format { "fieldname": "ASC", "fieldtwo": "DESC" }
 * @returns String in the format fieldname|ASC,fieldtwo|DESC
 */
export const serializeSort = (obj) =>
  Object.keys(obj)
    .map((key) => (obj[key] === 'DESC' ? `-${key}` : key))
    .join('|');

/**
 * Un-serializes a sort object from URL
 * @param String strj String in the format fieldname|ASC,fieldtwo|DESC
 * @returns Key-value map in the format { "fieldname": "ASC", "fieldtwo": "DESC" }
 */
export const unserializeSort = (str) =>
  str && str.length
    ? str
        .split('|')
        .filter(Boolean)
        .reduce((out, s) => {
          if (/^-/.test(s)) {
            out[s.replace(/^-/, '')] = 'DESC';
          } else {
            out[s] = 'ASC';
          }
          return out;
        }, {})
    : {};

export const dangerouslyStripHTML = (str) => {
  if (!str) {
    return '';
  }

  return `${str}`
    .replace(/<\/?("[^"]*"|'[^']*'|[^>])*(>|$)/g, '')
    .replace(/\s+/, ' ')
    .replace(/\n/g, '');
};

export const excerpt = (html, size = 16, moreChar = '…') => {
  const words = dangerouslyStripHTML(html).split(/\s+/);
  if (words.length > size) {
    let excerpt = words.slice(0, size).join(' ');
    excerpt = excerpt.replace(/[^a-z]+$/i, moreChar);
    return excerpt;
  } else {
    return words.join(' ');
  }
};

export const fetchJSON = async (...args) => {
  const response = await fetch(...args);
  if (response.ok) {
    return response.json();
  }
  throw new Error(`FetchJSON Error ${response.status} ${response.statusText}`);
};

export const triggerDownload = async (uri, name) => {
  const response = await fetch(uri);
  if (response.ok) {
    const data = await response.blob();
    const dataURL = URL.createObjectURL(data);
    const link = document.createElement('a');
    link.download = name;
    link.href = dataURL;
    link.click();
  }
};

export const filterObj = (obj, keys) =>
  keys.reduce((result, key) => {
    if (obj[key] !== undefined) {
      result[key] = obj[key];
    }
    return result;
  }, {});

export const generateRandomCode = (length = 6) =>
  `${crypto.getRandomValues(new Uint32Array(1))[0] % 10 ** length}`.padStart(
    length,
    '0'
  );

export const doctorName = (doc) => {
  const name =
    doc.kind === 'doctor'
      ? `${doc.honorific} ${doc.name}`
      : `${doc.clinicName}`;
  return name.trim();
};

export const doctorSalesforceObjectId = (doc) =>
  [doc.salesforceContactId, doc.salesforceAccountId].filter(Boolean);

export const isReasonableDate = (date) => {
  if (!date) {
    return false;
  }

  if (!(date instanceof Date)) {
    if (date === '1970-01-01T00:00:00.000Z') {
      return false;
    }
    date = new Date(date);
  }
  return date.getTime() !== 0;
};

export const clamp = (num, min, max) => Math.max(min, Math.min(max, num || 0));

export const validationOptions = (options) => {
  if (validationDisabled) {
    return {};
  }

  return options;
};

// Note: This has taken me literally hours. Don't poke the monster.
const addressRegex =
  /^(?:(?:(.+?)([ \t\-,;`~_./\\]+))?((?!\d+(?:st|nd|rd|th|e|ère|ere|ème|eme|°|ᵉ)[ \t])[0-9][0-9a-z]*)[ \t]+((?=(?:\d+(?:st|nd|rd|th|e|ère|ere|ème|eme|°|ᵉ)[ \t]|\D)).+?)|\s*(p\W?o\W?\s*box)\s*(\d+(?:[ \t].+)?)\s*)$/i;

export const parseStreetAddress = (address) => {
  let unitNumber = null;
  let streetNumber = null;
  let streetName = null;
  try {
    const match = addressRegex.exec(address?.trim())?.map((m) => m?.trim());
    if (match) {
      let separator, poBox, boxNo;
      [, unitNumber, separator, streetNumber, streetName, poBox, boxNo] = match;
      // Restore hyphenated street number if necessary
      if (
        separator === '-' &&
        /^[0-9]+$/.test(streetNumber) &&
        /^[0-9]+$/.test(unitNumber)
      ) {
        streetNumber = `${unitNumber}-${streetNumber}`;
        unitNumber = null;
      } else if (
        /\d[0-9a-z]*/i.test(unitNumber) &&
        separator.trim() === '' &&
        streetTypes.includes(
          streetName.replace(/\W+$/, '').trim().toLowerCase()
        )
      ) {
        // Numbered, suffixed st. Unfortunately common case.
        streetName = `${streetNumber} ${streetName}`;
        streetNumber = unitNumber;
        unitNumber = null;
      }
      // PO Box exceptions
      if (poBox && boxNo) {
        // Standard PO Box
        streetNumber = boxNo;
        streetName = poBox;
        unitNumber = null;
      } else if (likelyPOBox(unitNumber) && streetNumber && streetName) {
        // PO Box with station number, etc. caught by "normal" address filter
        streetNumber = `${streetNumber} ${streetName}`;
        streetName = unitNumber;
        unitNumber = null;
      }
    }
  } catch (err) {
    /* ignore */
  }
  return { unitNumber, streetNumber, streetName };
};

export const likelyPOBox = (str) => /\s*(p\W?o\W?\s*box)\s*/i.test(str);

export const cleanPhoneNumber = (phone) =>
  `${phone}`.replace(/(^\+?1|[^0-9]+)/g, '');

export const formatE164Phone = (phone) => {
  phone = cleanPhoneNumber(phone);
  if (!phone) {
    return null;
  }
  return `+1${phone}`;
};

export const setCookie = (name, value, days, path = '/') => {
  let expires = '';
  if (days) {
    const date = new Date();
    date.setTime(date.getTime() + days * 86400000);
    expires = `; expires=${date.toGMTString()}`;
  }
  document.cookie = `${name}=${encodeURIComponent(value)}${expires}; path=${path}`;
};

export const getCookie = (name) => {
  const nameEQ = name + '=';
  const cookie = document.cookie.split(';').find(function (c) {
    return c.replace(/^ +/, '').indexOf(nameEQ) === 0;
  });
  return cookie
    ? decodeURIComponent(`${cookie}`.substring(nameEQ.length + 1))
    : null;
};

export const deleteCookie = (name, path = '/') => setCookie(name, '', -1, path);

export const getPersistedUTMData = () => {
  const url = getCookie('landing_url');
  if (url) {
    const params = new URLSearchParams(url.slice(url.indexOf('?') + 1));
    const result = {};
    params.forEach((val, key) => {
      if (result[key]) {
        result[key] = `${result[key]},${val}`;
      } else {
        result[key] = val;
      }
    });
    return result;
  }
  return null;
};

export const abbrevMoney = (str, lng) => {
  const parts = numFormatParts(lng);
  if (typeof str !== 'string') {
    str = formatCurrency(str, true, false, lng);
  }
  const bRegex = new RegExp(
    `${parts.group}[0-9]{3}${parts.group}[0-9]{3}${parts.group}[0-9]{3}`
  );
  const mRegex = new RegExp(`${parts.group}[0-9]{3}${parts.group}[0-9]{3}`);
  const kRegex = new RegExp(`${parts.group}[0-9]{3}`);

  if (bRegex.test(str)) {
    return str.replace(bRegex, 'B');
  } else if (mRegex.test(str)) {
    return str.replace(mRegex, 'M');
  } else if (kRegex.test(str)) {
    return str.replace(kRegex, 'K');
  }
};

export const debounce = (fn, time = 300) => {
  let timeout;
  return (...args) => {
    if (timeout) {
      clearTimeout(timeout);
    }
    timeout = setTimeout(() => fn(...args), time);
  };
};

export const formatPreferredName = (
  firstName,
  lastName,
  preferredName,
  patientName
) => {
  if (preferredName && firstName !== preferredName) {
    return `${preferredName} ${lastName}`;
  } else if (patientName) {
    return patientName;
  } else {
    return `${firstName} ${lastName}`;
  }
};

const translatePronouns = (pronouns, t) => {
  const translatedPronouns = {
    'He/Him': t('pronouns.heHim', 'He/Him'),
    'She/Her': t('pronouns.sheHer', 'She/Her'),
    'They/Them': t('pronouns.theyThem', 'They/Them'),
    'He/They': t('pronouns.heThey', 'He/They'),
    'She/They': t('pronouns.sheThey', 'She/They')
  };

  return translatedPronouns[pronouns];
};

export const translateStatus = (status, t) => {
  const translatedStatuses = {
    'Approved': t('application.decisions.approved', 'Approved'),
    'Partial Approval': t(
      'application.decisions.partiallyApproved',
      'Partially Approved'
    ),
    'Conditionally Approved': t(
      'application.decisions.conditionallyApproved',
      'Conditionally Approved'
    ),
    'Declined': t('application.decisions.declined', 'Declined'),
    'Pending': t('application.decisions.pending', 'Pending')
  };

  return translatedStatuses[status] || translatedStatuses['Pending'];
};

export const formatPronouns = (
  pronouns,
  otherPronouns,
  t,
  brackets = false
) => {
  if (pronouns === 'Not Listed' && otherPronouns) {
    return brackets ? ` (${otherPronouns})` : otherPronouns;
  } else if (pronouns) {
    const translated = translatePronouns(pronouns, t);
    return brackets ? ` (${translated})` : translated;
  }
  return '';
};

export const translateLeadSource = (leadSource, t) => {
  const translatedSources = {
    'Merchant Link': t('leadSource.merchantLink', 'Merchant Link'),
    'Merchant-Led Journey': t(
      'leadSource.merchantLedJourney',
      'Merchant-Led Journey'
    ),
    'Website': t('leadSource.website', 'Website'),
    'Consultation Request': t(
      'leadSource.consultationRequest',
      'Consultation Request'
    )
    /*
    'Advertisement': t('leadSource.advertisement', 'Advertisement'),
    'Customer Event': t('leadSource.customerEvent', 'Customer Event'),
    'Employee Referral': t('leadSource.employeeReferral', 'Employee Referral'),
    'External Referral': t('leadSource.externalReferral', 'External Referral'),
    'Google AdWords': t('leadSource.googleAdwords', 'Google AdWords'),
    'Drift Chat': t('leadSource.driftChat', 'Drift Chat'),
    'Purchased List': t('leadSource.purchasedList', 'Purchased List'),
    'Webinar': t('leadSource.webinar', 'Webinar'),
    'Website - Claim My Profile': t(
      'leadSource.claimProfile',
      'Website - Claim My Profile'
    ),
    'Facebook': t('leadSource.facebook', 'Facebook'),
    'Instagram': t('leadSource.instagram', 'Instagram'),
    'TikTok': t('leadSource.tiktok', 'TikTok'),
    'Partner': t('leadSource.partner', 'Partner'),
    'Trade Show': t('leadSource.tradeShow', 'Trade Show'),
    'Newsletter Subscriber': t(
      'leadSource.newsletter',
      'Newsletter Subscriber'
    ),
    'Partnerships Email': t('leadSource.partnerships', 'Partnerships Email'),
    'Medicard Partner': t('leadSource.medicard', 'Medicard Partner'),
    'Marketplace': t('leadSource.marketplace', 'Marketplace'),
    'Embedded Widgets': t('leadSource.widgets', 'Embedded Widgets'),
    'Inbound Call': t('leadSource.call', 'Inbound Call'),
    'E-Commerce': t('leadSource.ecommerce', 'E-Commerce'),
    'Giveaway': t('leadSource.giveaway', 'Giveaway') */
  };

  if (translatedSources[leadSource]) {
    return translatedSources[leadSource];
  }

  return t('leadSource.other', 'Other');
};

export const maskStart = (str, maskChar = '•', showLength = 4, totalLength) => {
  totalLength = totalLength || str.length;
  if (totalLength < showLength) {
    totalLength = showLength * 2;
  }

  const visible = str.slice(-showLength);
  return (
    Array(totalLength - visible.length)
      .fill(maskChar)
      .join('') + visible
  );
};

export const ensureInt = (number) => {
  if (typeof number === 'number') {
    return Math.round(number);
  } else if (typeof number === 'string') {
    const val = parseInt(number.replace?.(/[^0-9.]/g, ''));
    return isNaN(val) ? 0 : val;
  } else {
    return 0;
  }
};

export const ensureFloat = (number) => {
  if (typeof number === 'number') {
    return number;
  } else if (typeof number === 'string') {
    const val = parseFloat(number.replace?.(/[^0-9.]/g, ''));
    return isNaN(val) ? 0 : val;
  } else {
    return 0;
  }
};

// Capitalizes the first letter of the string
export const capitalizeFirstLetter = ([first = '', ...rest]) => {
  return [first.toUpperCase(), ...rest].join('');
};

// Capitalizes the first letter of each word
export const capitalizeWords = (str) =>
  str.replace(/(^\w{1})|(\s+\w{1})/g, (letter) => letter.toUpperCase());

// Gets the extension from a URL or filename, kind of cheekily
export const getExtension = (url) => {
  const parts = /\.([^.]+)(\?|$)/.exec(url);
  return parts?.[1] || '';
};
export const getFilename = (url) => {
  const parts = /(\/?|^)([^\\/]+)(\?|$)/.exec(url);
  return parts?.[2] || '';
};

export const convertSalesforceDate = (date) => {
  if (!date) return null;
  const [year, month, day] = date.split('-').map(Number);
  return new Date(year, month - 1, day);
};

export const parseLocalDate = (input) => {
  if (!input) {
    return null;
  }
  if (typeof input === 'string') {
    const [year, month, day] = input.split('-').map(Number);
    return new Date(year, month - 1, day); // Local midnight
  }
  if (input instanceof Date) {
    return new Date(input.getFullYear(), input.getMonth(), input.getDate());
  }
};

export const isUUID = (str) =>
  /[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[0-9a-f]{4}-[0-9a-f]{12}/.test(
    `${str}`.toLowerCase()
  );

const SEVEN_DAYS = 604800000;
export const calculateFirstPaymentDate = (currentDate, preferredDay) => {
  const today = currentDate || new Date();
  const thisMonthDate = new Date(
    today.getFullYear(),
    today.getMonth(),
    preferredDay,
    today.getHours(),
    today.getMinutes()
  );
  const nextMonthDate = new Date(
    today.getFullYear(),
    today.getMonth() + 1,
    preferredDay,
    today.getHours(),
    today.getMinutes()
  );
  if (thisMonthDate - today > SEVEN_DAYS) {
    return thisMonthDate;
  } else if (nextMonthDate - today > SEVEN_DAYS) {
    return nextMonthDate;
  } else {
    nextMonthDate.setMonth(nextMonthDate.getMonth() + 1);
    return nextMonthDate;
  }
};

export const calculateLastPaymentDate = (firstPaymentDate, term) => {
  const lastPaymentDate = new Date(
    firstPaymentDate.getFullYear(),
    firstPaymentDate.getMonth(),
    firstPaymentDate.getDate(),
    firstPaymentDate.getHours(),
    firstPaymentDate.getMinutes()
  );
  lastPaymentDate.setMonth(lastPaymentDate.getMonth() + term - 1);
  return lastPaymentDate;
};

/* Gets LeadID and/or Email address from the callback URL, if present */
export const callbackUrlInfo = (callbackUrl, account) => {
  const result = { callbackUrl, account };
  if (callbackUrl) {
    try {
      const callback = new URL(callbackUrl);
      if (callback.searchParams.has('account')) {
        result.account = callback.searchParams.get('account');
        callback.searchParams.delete('account');
      }
      result.callbackUrl = callback.toString();
      const [, leadId] =
        /\/(00Q[a-zA-Z0-9]+)(\/|$)/.exec(callback.pathname) || [];
      if (leadId) {
        result.leadId = leadId;
      }
    } catch (err) {
      /* ignore */
    }
  }
  return result;
};

/**
 * Checks a url against a list of valid URLs, wildcards supported.
 * @param {String[]} validURLs The list of valid urls to check against. Wildcards (*) are supported, and match one or more characters
 * @param {String} urlToCheck The url to check against
 * @returns true if any of the URLs match, false otherwise
 */
export const checkRedirectURLs = (validURLs, urlToCheck) => {
  try {
    const url = new URL(urlToCheck);
    url.search = ''; // Ignore additional search params so they can be forwarded.
    urlToCheck = url.toString();
  } catch {
    return false;
  }
  urlToCheck = urlToCheck.replace(/\/$/, '');
  return validURLs.some((validUrl) => {
    const parsedRegex = validUrl
      .replace(/\/$/, '')
      .split(/\*/g)
      .map((part) => escapeRegExp(part))
      .join('.+?');
    const validRegexp = new RegExp(`^${parsedRegex}$`, 'i');
    return validRegexp.test(urlToCheck);
  });
};
